import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';

import { DetailArrowButton } from './DetailArrowButton';
import { IdCell, NameCell } from './IdentityCells';
import { RowActionsMenu } from './RowActionsMenu';
import { ColumnDef } from './StructureTable.types';
import { StructureTableRowProps } from './StructureTableRow.props';
import { formatFriendlyDateTime } from '../../../utils/common';
import { displayNodeTypeName } from '../../../utils/node-type';
import { TablePill } from '../../atoms/TablePill';
import { TagCell, TagCellItem } from '../../molecules/TagCell';

// Row fields that hold ISO timestamps and should render as
// "8 May 2026, 1:13 AM" instead of the raw ISO string. Pages that
// supply a column-level `render` function (e.g. RoleList) bypass this
// branch — see `if (col.render) return col.render(row)` below.
const DATE_FIELD_KEYS: ReadonlySet<string> = new Set(['createdAt', 'updatedAt']);

const CELL_SX = {
  fontSize: '11px',
  py: 0.25,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

const MULTILINE_CELL_SX = {
  fontSize: '11px',
  whiteSpace: 'pre-line' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical' as const,
};

const ActivePill: React.FC<{ active: boolean }> = ({ active }) => (
  <TablePill tone={active ? 'positive' : 'neutral'} ariaLabel={active ? 'Active' : 'Inactive'}>
    {active ? 'Active' : 'Inactive'}
  </TablePill>
);

const NodeTypeChip: React.FC<{ label: string }> = ({ label }) =>
  // Neutral, not the brand teal: a node type is a category label, not a state
  // the reader has to act on, and `primary.light` on `primary.dark` measured
  // 2.43:1 against a 4.5:1 requirement.
  label ? <TablePill>{label}</TablePill> : null;

export const StructureTableRow: React.FC<StructureTableRowProps> = React.memo(
  ({ row, columns, even, detailHref, rowActions, onAction, itemLabel = 'node', onOpen }) => {
    const theme = useTheme();
    const nodeId = row.id as number | undefined;
    const accent = theme.palette.v4.accent;

    const getRowBg = () => {
      if (even) return theme.palette.v4.tableEven;
      return 'background.paper';
    };

    const renderCell = (col: ColumnDef) => {
      if (col.render) return col.render(row);

      const value = row[col.key];

      if (col.key === 'id' && nodeId != null) {
        return (
          <IdCell
            row={row}
            detailHref={detailHref}
            accent={accent}
            onOpen={onOpen}
            nodeId={nodeId}
          />
        );
      }

      if (col.key === 'name' && typeof value === 'string') {
        return (
          <NameCell
            row={row}
            detailHref={detailHref}
            accent={accent}
            onOpen={onOpen}
            value={value}
          />
        );
      }

      if (col.key === 'nodeTypeName' && typeof value === 'string') {
        return <NodeTypeChip label={displayNodeTypeName(value)} />;
      }

      if (col.key === 'isActive') {
        const active = value === true || value === 'Yes' || value === 'true';
        return <ActivePill active={active} />;
      }

      if (col.key === 'structureTags' && Array.isArray(value)) {
        return <TagCell tags={value as TagCellItem[]} />;
      }

      if (col.key === 'mainAddress' || col.key === 'allAddresses' || col.key === 'allContacts') {
        const text = value != null ? String(value) : '';
        return (
          <Box sx={MULTILINE_CELL_SX} title={text}>
            {text}
          </Box>
        );
      }

      if (col.key === 'tradingHrs' && Array.isArray(value)) {
        return value
          .map((d: { tradingHrs?: string }) => d.tradingHrs)
          .filter(Boolean)
          .join(', ');
      }

      if (col.key === 'siteSchema' && value && typeof value === 'object') {
        return (value as { name?: string }).name ?? '';
      }

      if (DATE_FIELD_KEYS.has(col.key)) {
        return formatFriendlyDateTime(
          typeof value === 'string' || value instanceof Date ? value : null
        );
      }

      return value != null ? String(value) : '';
    };

    return (
      <TableRow
        sx={{
          bgcolor: getRowBg(),
          '&:hover': { bgcolor: theme.palette.v4.tableHover },
          transition: 'background-color 0.15s ease',
        }}
      >
        {columns.map((col) => (
          <TableCell
            key={col.key}
            sx={{
              ...CELL_SX,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(col.width !== undefined
                ? { width: col.width, minWidth: col.width, maxWidth: col.width }
                : {}),
            }}
          >
            {renderCell(col)}
          </TableCell>
        ))}
        <TableCell
          sx={{
            ...CELL_SX,
            position: 'sticky',
            right: 0,
            bgcolor: 'inherit',
            zIndex: 1,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
          >
            <DetailArrowButton
              row={row}
              detailHref={detailHref}
              itemLabel={itemLabel}
              nodeId={nodeId}
              onOpen={onOpen}
            />
            <RowActionsMenu
              nodeId={nodeId ?? 0}
              actions={rowActions}
              onAction={onAction}
              itemLabel={itemLabel}
            />
          </Box>
        </TableCell>
      </TableRow>
    );
  }
);

StructureTableRow.displayName = 'StructureTableRow';
